import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge, Button, Card, SmartImage } from '@atoms/index';
import type { Order, OrderStatus } from '@/types';
import { formatOrderStatus } from '@utils/formatters';
import { formatCurrency } from '@utils/currency';

type OrderRestaurantSummary = {
  id: string;
  name: string;
  logoUrl?: string | null;
};

export type OrderCardOrder = Order & {
  restaurant?: OrderRestaurantSummary | null;
};

const USER_CANCELABLE_STATUSES: OrderStatus[] = ['CREATED', 'PENDING', 'SCHEDULED'];

interface OrderCardProps {
  order: OrderCardOrder;
  variant: 'active' | 'past';
  onReorder?: (order: OrderCardOrder) => void;
  onRate?: (order: OrderCardOrder) => void;
  onCancel?: (order: OrderCardOrder) => void;
  isReorderLoading?: boolean;
  isCancelLoading?: boolean;
}

const getStatusBadgeVariant = (status: OrderStatus): 'default' | 'success' | 'warning' | 'danger' | 'info' => {
  switch (status) {
    case 'PREPARING':
      return 'warning';
    case 'READY':
      return 'info';
    case 'ON_WAY':
      return 'info';
    case 'DELIVERED':
      return 'success';
    case 'CANCELLED':
      return 'default';
    case 'CREATED':
    case 'PENDING':
    case 'SCHEDULED':
    default:
      return 'default';
  }
};

const formatOrderDate = (iso: string) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('tr-TR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const OrderCard = ({ order, variant, onReorder, onRate, onCancel, isReorderLoading, isCancelLoading }: OrderCardProps) => {
  const canCancel = variant === 'active' && USER_CANCELABLE_STATUSES.includes(order.status);
  const navigate = useNavigate();
  const reviewRating =
    typeof order.reviewRating === 'number' ? order.reviewRating : order.review?.rating ?? null;
  const isReviewed = Boolean(order.isReviewed || order.review || reviewRating != null);

  const itemLines = useMemo(() => {
    const items = Array.isArray(order.items) ? order.items : [];
    const visible = items.slice(0, 2);
    const remaining = Math.max(0, items.length - visible.length);

    return {
      visible: visible.map((item, index) => ({
        id: item.menuItemId || `item-${index}`,
        name: item.menuItemName || 'Ürün',
        index,
      })).filter((mapped) => mapped.name !== 'Ürün' || mapped.id),
      remaining,
    };
  }, [order.items]);

  const restaurantName = order.restaurant?.name || 'Restoran';
  const restaurantLogoUrl = order.restaurant?.logoUrl || undefined;

  return (
    <Card className="p-0 overflow-hidden">
      <div className="p-4">
        <div className="flex items-start gap-3">
          <div className="w-12 h-12 rounded-xl overflow-hidden bg-gray-100 border border-gray-200 flex-shrink-0">
            <SmartImage
              src={restaurantLogoUrl}
              alt={restaurantName}
              aspectRatio="square"
              objectFit="cover"
              className="w-full h-full"
            />
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="font-bold text-gray-900 truncate">{restaurantName}</p>
                <p className="text-xs text-gray-500">{formatOrderDate(order.createdAt)}</p>
              </div>
              <Badge variant={getStatusBadgeVariant(order.status)} size="sm">
                {formatOrderStatus(order.status)}
              </Badge>
            </div>

            <div className="mt-3 space-y-1">
              {itemLines.visible.length > 0 ? (
                itemLines.visible.map((item) => (
                  <p key={item.id} className="text-sm text-gray-700 truncate">
                    {item.name}
                  </p>
                ))
              ) : (
                <p className="text-sm text-gray-500">Ürün bilgisi alınamadı</p>
              )}

              {itemLines.remaining > 0 && (
                <p className="text-sm text-gray-500">+ {itemLines.remaining} ürün daha</p>
              )}
            </div>

            <div className="mt-3 flex items-center justify-between">
              <p className="font-bold text-gray-900">{formatCurrency(Number(order.total || 0))}</p>
              <p className="text-xs text-gray-500">{order.orderNumber ? `#${order.orderNumber}` : `#${order.id.slice(0, 8)}`}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="px-4 py-3 border-t border-gray-100 bg-gray-50/50 flex items-center justify-end gap-2">
        {variant === 'active' ? (
          <>
            <Button size="sm" variant="primary" onClick={() => navigate(`/orders/${order.id}`)}>
              Takip Et
            </Button>
            {canCancel && (
              <Button
                size="sm"
                variant="secondary"
                onClick={() => onCancel?.(order)}
                disabled={Boolean(isCancelLoading)}
                isLoading={Boolean(isCancelLoading)}
              >
                İptal Et
              </Button>
            )}
          </>
        ) : (
          <>
            <Button size="sm" variant="secondary" onClick={() => navigate(`/orders/${order.id}`)}>
              Detay
            </Button>
            {order.status === 'DELIVERED' && !isReviewed && (
              <Button size="sm" variant="secondary" onClick={() => onRate?.(order)}>
                Değerlendir
              </Button>
            )}
            {order.status === 'DELIVERED' && isReviewed && (
              <Badge variant="success" size="sm">
                ⭐ {reviewRating ?? order.review?.rating ?? 0}/5
              </Badge>
            )}
            <Button
              size="sm"
              variant="primary"
              onClick={() => onReorder?.(order)}
              disabled={Boolean(isReorderLoading)}
              isLoading={Boolean(isReorderLoading)}
            >
              Tekrar Sipariş Ver
            </Button>
          </>
        )}
      </div>
    </Card>
  );
};

export default OrderCard;

